import AxiosInterceptors from 'axios'

let axios = AxiosInterceptors

const createAxios = token => {
  axios = AxiosInterceptors.create({
    baseURL: `${process.env.REACT_APP_LARAVEL_API_ENDPOINT}`,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })

  axios.interceptors.response.use(
    response => response,
    error =>
      Promise.reject(
        (error.response && error.response.data) || 'Something went wrong!',
      ),
  )
}

const loadSafetyData = async ({
  partner,
  vehicle_visibility,
  divisions,
  departments,
  sections,
  start_at,
  end_at,
  plants,
  vehicle_type,
  event_type,
  incident_lcc_status,
  is_lcc,
  only_has_dp,
  is_manual,
  order,
  order_by,
  search,
  page = 1,
  pageSize = 20,
}) => {
  try {
    const payload = {
      partner,
      vehicle_visibility,
      division: divisions,
      department: departments,
      section: sections,
      start_at: start_at.clone().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end_at: end_at.clone().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      plant_ids: plants,
      vehicle_type,
      only_has_dp,
      event_type,
      note: '-1',
      incident_lcc_status,
      is_lcc,
      is_manual,
      order,
      order_by,
      search,
      page,
      page_size: pageSize,
    }

    const res = await axios.post(
      `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/alert-dashboard`,
      payload,
    )

    if (res.data == null) {
      return {
        data: [],
        total: 0,
        page,
        pageSize,
      }
    }

    const normalized = normalizeSafetyDataResponse(res.data, { page, pageSize })

    return normalized
  } catch (error) {
    console.log('api', error)
    throw new Error('Failed to load safety data.')
  }
}

const normalizeSafetyDataResponse = (
  response,
  { page: requestedPage, pageSize: requestedPageSize },
) => {
  if (Array.isArray(response)) {
    const total = response.length
    const offset = (requestedPage - 1) * requestedPageSize

    return {
      data: response.slice(offset, offset + requestedPageSize),
      total,
      page: requestedPage,
      pageSize: requestedPageSize,
    }
  }

  const items =
    response.data ||
    response.items ||
    response.rows ||
    response.results ||
    response.result ||
    []

  const pagination = response.pagination || {}

  return {
    data: Array.isArray(items) ? items : [],
    total:
      response.total ||
      response.count ||
      response.total_count ||
      pagination.total ||
      (Array.isArray(items) ? items.length : 0),
    page:
      response.page ||
      response.current_page ||
      pagination.page ||
      pagination.current ||
      requestedPage,
    pageSize:
      response.pageSize ||
      response.page_size ||
      response.per_page ||
      response.limit ||
      pagination.pageSize ||
      pagination.page_size ||
      pagination.per_page ||
      requestedPageSize,
  }
}

const loadPartner = async ({ company_id }) => {
  const { data } = await axios.post(`/getpartnersafetydashboard`, {
    company_id,
  })

  return data
}

const loadDivision = async ({ division_visibility }) => {
  const { data } = await axios.post(`/getdivision`, {
    division_visibility,
    comp: [2, 3, 4, 5, 6],
  })

  return data
}

const loadDepartment = async ({ department_visibility, divisions }) => {
  const { data } = await axios.post(`/getdepartment`, {
    department_visibility,
    division: divisions,
  })

  return data
}

const loadSection = async ({ section_visibility, departments }) => {
  const { data } = await axios.post(`/getsection`, {
    section_visibility,
    department: departments,
  })

  return data
}

const loadPlant = async ({
  location_visibility,
  section_visibility,
  sections,
}) => {
  const { data } = await axios.post(`/getplantforconcretedashboard`, {
    location_visibility,
    section_visibility,
    section: sections,
  })

  return data
}

const loadVehicleType = async () => {
  const {
    data: { data },
  } = await axios.get(`/cpac-truck-sharing-vehicle-type`)
  return data
}

const loadEventType = async () => {
  const {
    data: { data },
  } = await axios.get(`/event-type`)

  return data
}

const loadVehicleById = async ({ vehicle_id }) => {
  const {
    data: { data },
  } = await axios.get(`/vehicle/${vehicle_id}`)

  return data
}

const loadVdoPlayback = async ({ vehicle_id }) => {
  const {
    data: { data },
  } = await axios.post(`/video-playback/${vehicle_id}`)
  return data
}

const loadCurrentLocation = async ({ vehicle_id }) => {
  const {
    data: { markers },
  } = await axios.post(
    `${process.env.REACT_APP_REDIS_API_ENDPOINT}/getvehiclefortrackingmap`,
    {
      company_id: 54,
      department_id: 0,
      division_id: 0,
      gps_vendor_id: 0,
      plant_id: [],
      search_name: '',
      section_id: 0,
      selected_tracking_columns: [
        'vehicle_code',
        'plate_no',
        'velocity',
        'vehicle_status',
        'latest_pos_update_at',
        'driver_license_swiping_status',
        'gps_active_at',
        'flow',
        'time_diff',
        'device_code',
        'drum_direction',
        'company_name',
        'plant',
        'lastest_driver',
      ],
      speed_range: [0, 200],
      time_range: [0, 240],
      truck_size: '',
      vehicle_code: '',
      vehicle_group: [],
      vehicle_status: [
        'running',
        'stop-with-engine-on',
        'stop-with-engine-off',
        'connection-error',
      ],
      vehicle_type: [],
      vehicle_visibility: [vehicle_id],
    },
  )

  return markers[0]
}

const loadLccConfig = async () => {
  const { data } = await axios.post(`/lcc-config`)
  return data
}

const loadLccConfigAll = async () => {
  const { data } = await axios.post(`lcc-config/all`)
  return data
}

const loadManualChoices = async () => {
  const {
    data: { data },
  } = await axios.get(`/lcc-manual-choice`)
  return data
}

const createManualChoice = async payload => {
  const { data } = await axios.post(`/lcc-manual-choice`, payload)
  return data
}

const updateManualChoice = async (id, payload) => {
  const { data } = await axios.put(`/lcc-manual-choice/${id}`, payload)
  return data
}

const deleteManualChoice = async id => {
  const { data } = await axios.delete(`/lcc-manual-choice/${id}`)
  return data
}

const loadLccReason = async () => {
  const {
    data: { data },
  } = await axios.get(`lcc-reason`)

  return data
}

const updateLccCause = async payload => {
  const { data } = await axios.post(
    `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/lcc-cause`,
    payload,
  )
  return data
}

const updateLccCauses = async payload => {
  const { data } = await axios.post(
    `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/lcc-causes`,
    payload,
  )
  return data
}

const updateVendorCause = async payload => {
  const { data } = await axios.post(
    `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/vendor-cause`,
    payload,
  )
  return data
}

const updateVendorCauses = async payload => {
  const { data } = await axios.post(
    `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/vendor-causes`,
    payload,
  )
  return data
}

const saveNote = async payload => {
  const { data } = await axios.post(
    `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/note`,
    payload,
  )

  return data
}

const loadNoteHistory = async id => {
  const { data } = await axios.get(
    `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/note/${id}`,
  )

  return data
}
const loadReport = async payload => {
  try {
    const { data: res } = await axios.post(
      `${process.env.REACT_APP_API_REPORT_GOLANG}/reports/go/lccreport`,
      payload,
    )

    if (res.result && res.result !== '') {
      window.location = res.result
    } else {
      console.warn('No result URL returned.')
    }
  } catch (error) {
    console.error('Failed to load report:', error)
  }
}

const loadDivisionTree = async ({
  division_visibility,
  department_visibility,
  section_visibility,
  location_visibility,
}) => {
  try {
    const {
      data: { data },
    } = await axios.post(`/division/tree`, {
      comp: [2, 3, 4, 5, 6, 10],
      division_visibility,
      department_visibility,
      section_visibility,
      location_visibility,
    })

    return data
  } catch (error) {}
}

const loadVehicles = async ({ vehicle_visibility }) => {
  try {
    const {
      data: { data },
    } = await axios.post(`/getvehiclesbydivisionbranch`, {
      department: 'all',
      division: 'all',
      partner: 'all',
      plant: ['all'],
      section: 'all',
      section_visibility: [],
      vehicle_group: 'all',
      vehicle_visibility,
      gps_vendor_id_only: true,
    })

    return data
  } catch (error) {}
}

const checkDupEvent = async payload => {
  try {
    const { vehicle_ids, event_type_ids, date, id } = payload

    const {
      data: { data },
    } = await axios.post(
      `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/check-duplicate/${id}`,
      {
        vehicle_ids,
        event_type_ids,
        date,
      },
    )

    return data
  } catch (error) {}
}

const addEvent = async payload => {
  try {
    const { vehicle_ids, event_type_ids, user_id, file_url, date } = payload

    const {
      data: { data },
    } = await axios.post(
      `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/add`,
      {
        vehicle_ids,
        event_type_ids,
        file_url,
        user_id,
        date,
      },
    )

    return data
  } catch (error) {}
}

const editEvent = async payload => {
  try {
    const { vehicle_ids, event_type_ids, user_id, file_url, date, id } = payload

    const {
      data: { data },
    } = await axios.post(
      `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/edit/${id}`,
      {
        vehicle_ids,
        event_type_ids,
        file_url,
        user_id,
        date,
      },
    )

    return data
  } catch (error) {}
}

const uploadFile = async files => {
  const formData = new FormData()

  files.forEach(file => {
    formData.append('files[]', file)
  })

  formData.append('id', '1')
  const { data } = await axios.post('/lcc/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  return data
}

const deleteEvent = async id => {
  try {
    const {
      data: { data },
    } = await axios.post(
      `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/del/${id}`,
    )

    return data
  } catch (error) {}
}

const updateFile = async (files, id) => {
  try {
    const {
      data: { data },
    } = await axios.post(
      `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/file/${id}`,
      { file_url: files },
    )

    return data
  } catch (error) {}
}

const assign = async payload => {
  const { ids, assignee } = payload
  const {
    data: { data },
  } = await axios.post(
    `${process.env.REACT_APP_GO_API_ENDPOINT}/logistic-command-center/assignee`,
    {
      ids,
      assignee,
    },
  )

  return data
}

const loadEventSafty = async () => {
  const {
    data: { data },
  } = await axios.get(`/event_safties`)

  return data
}

export {
  createAxios,
  loadManualChoices,
  createManualChoice,
  updateManualChoice,
  deleteManualChoice,
  loadSafetyData,
  loadPartner,
  loadDivision,
  loadDivisionTree,
  loadDepartment,
  loadSection,
  loadPlant,
  loadVehicleType,
  loadEventType,
  loadVehicleById,
  loadVdoPlayback,
  loadCurrentLocation,
  loadLccConfig,
  loadLccReason,
  updateLccCause,
  updateVendorCause,
  saveNote,
  loadNoteHistory,
  loadReport,
  updateLccCauses,
  updateVendorCauses,
  loadVehicles,
  addEvent,
  editEvent,
  checkDupEvent,
  loadLccConfigAll,
  uploadFile,
  deleteEvent,
  updateFile,
  assign,
  loadEventSafty,
}
